import { useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/apiClient";
import { Dialog, DialogPortal, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";

const GateConfirmation = ({ id, checked, onCheckedChange, children }) => (
  <div className={`gate-confirmation ${checked ? "is-checked" : ""}`}>
    <Checkbox id={id} data-testid={id} checked={checked} onCheckedChange={onCheckedChange} required />
    <label htmlFor={id}>{children}</label>
  </div>
);

export default function ResearcherGate({ onVerified }) {
  const titleRef = useRef(null);
  const [age, setAge] = useState(false);
  const [researcher, setResearcher] = useState(false);
  const [terms, setTerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const submitting = useRef(false);
  const ready = age && researcher && terms;

  const enter = async (event) => {
    event.preventDefault();
    if (!ready || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await api.post("/researcher-consents", {
        age_confirmed: age, researcher_confirmed: researcher,
        terms_accepted: terms, consent_version: "ethos-verification-v1",
      }, { timeout: 15000 });
      // Persist across visits (30 days) so returning users skip the gate.
      localStorage.setItem("ethos_verified", JSON.stringify({ at: Date.now() }));
      onVerified();
      setTimeout(() => window.scrollTo(0, 0), 60);
    } catch {
      setSaveError("We couldn't save your confirmation. Please check your connection and try again.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogPortal>
        <DialogPrimitive.Overlay className="gate-backdrop" />
        <DialogPrimitive.Content
          className="researcher-gate"
          data-testid="researcher-gate"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => { event.preventDefault(); titleRef.current?.focus(); }}
        >
          <div className="gate-panel">
            <header className="gate-header">
              <img src="/assets/ethos-research-labs-logo.webp" alt="Ethos Research Labs" width="990" height="509" className="gate-logo" data-testid="gate-logo" />
              <DialogTitle asChild>
                <h1 ref={titleRef} tabIndex={-1} className="gate-title" data-testid="gate-title">Welcome to Ethos Research Labs</h1>
              </DialogTitle>
              <div className="gate-rule" aria-hidden="true" />
              <h2 className="gate-age-title" data-testid="gate-age-title">VERIFY YOUR AGE</h2>
              <DialogDescription className="gate-description" data-testid="gate-description">
                Confirm you are 21 and older to enter catalog.
              </DialogDescription>
            </header>

            <p className="gate-notice" data-testid="gate-fda-notice">
              <strong>FDA NOTICE</strong> — All products are sold strictly for <em>in vitro</em> laboratory research only. Not approved by the FDA for human use, veterinary use, or consumption of any kind.
            </p>

            <form onSubmit={enter} data-testid="gate-form" aria-busy={saving}>
              <fieldset disabled={saving} className="min-w-0">
              <fieldset className="gate-confirmations">
                <legend className="sr-only">Required confirmations</legend>
                <GateConfirmation id="gate-age-checkbox" checked={age} onCheckedChange={setAge}>
                  I confirm that I am <strong>21 years of age or older.</strong>
                </GateConfirmation>
                <GateConfirmation id="gate-researcher-checkbox" checked={researcher} onCheckedChange={setResearcher}>
                  I confirm I am a researcher and any purchase is for <strong>legitimate research purposes only.</strong>
                </GateConfirmation>
                <GateConfirmation id="gate-terms-checkbox" checked={terms} onCheckedChange={setTerms}>
                  I agree to comply with all <strong>terms, conditions, and applicable laws</strong> governing this website and its products.
                </GateConfirmation>
              </fieldset>

              {saveError && <p role="alert" className="gate-email-error mb-3" data-testid="gate-save-error">{saveError}</p>}
              <Button type="submit" disabled={!ready || saving} data-testid="gate-enter-button" className="gate-enter-button">
                {saving ? <><Loader2 className="animate-spin" aria-hidden="true" /> SAVING CONFIRMATION</> : <>ENTER SITE <ArrowRight aria-hidden="true" /></>}
              </Button>
              </fieldset>
            </form>
            <a href="https://www.google.com" data-testid="gate-exit-link" className="gate-exit-link">Not a researcher? Exit</a>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
